import { useCallback, useMemo } from "react";

import { useMatchStore } from "shared/contexts";
import { useToast, useUser } from "shared/hooks";
import { useMatchApiService } from "shared/services";
import type { EntityRoles, FriendlyMatchData, IgnoreStateUpdate } from "shared/types";
import { RSVPStatusEnum } from "shared/types";
import type { CalendarEventModel, ManageCalendarEventsParams } from "shared/types/MatchStatusEnum";

import usePlatformEnv from "./usePlatformEnv";

import type { TeamInMatchRole } from "../types/CompetitionTeamsStandingModel";

const useMatch = () => {
	const matchService = useMatchApiService();

	const store = useMatchStore();
	const { setState } = useMatchStore();

	const { showToast } = useToast();

	const { updateMyAttendance } = useUser();

	const { isShowRoleToasts } = usePlatformEnv();

	const methods = useMemo(
		() => ({
			getMatchOverview: async (id: string) => {
				setState({
					loadingOverview: true
				});

				try {
					const match = await matchService.getMatchOverview(id);
					setState({ match });
					return match;
				} catch (err) {
					showToast({
						text: "Match couldn't be found",
						noIcon: true
					});
					console.error("Error: ", err);
					return null;
				} finally {
					setState({
						loadingOverview: false
					});
				}
			},
			getMatchInfo: async (id: string) => {
				setState({
					loadingInfo: true
				});

				try {
					const { match } = await matchService.getMatchInfo(id);

					setState({
						matchInfo: match
					});

					if (match?.auth?.roles && isShowRoleToasts) {
						showToast({
							text: `Roles for match: ${match.auth.roles.join(", ")}`,
							noIcon: true,
							position: "bottom-left"
						});
					}

					return match;
				} catch (err) {
					showToast({ text: "Error while fetching match info" });
					console.error("Error: ", err);
				} finally {
					setState({
						loadingInfo: false
					});
				}
			},
			setUserMatchRoles: (userMatchRoles: EntityRoles[]) => {
				setState({ userMatchRoles });
			},
			getAttendanceOverview: async (id: string) => {
				setState({
					loadingAttendanceOverview: true
				});

				try {
					const attendanceStatus = await matchService.getAttendanceOverview(id);

					setState({
						attendanceStatus
					});

					return attendanceStatus;
				} catch (err) {
					showToast({ text: "Error while fetching attendance overview" });
					console.error("Error: ", err);
				} finally {
					setState({
						loadingAttendanceOverview: false
					});
				}
			},
			getMatchTeams: async (id: string) => {
				setState({
					loadingTeams: true
				});

				try {
					const { match } = await matchService.getMatchTeams(id);

					return match;
				} catch (err) {
					showToast({ text: "Error while fetching match data" });
					console.error("Error: ", err);
				} finally {
					setState({
						loadingTeams: false
					});
				}
			},
			createFriendlyMatch: async (data: FriendlyMatchData) => {
				setState({
					loadingCreateFriendlyMatch: true
				});

				try {
					const { match } = await matchService.createFriendlyMatch(data);

					showToast({ text: "Friendly match was successfully created", noIcon: true });

					return match;
				} catch (err) {
					showToast({ text: "Error while creating friendly match" });
					console.error("Error: ", err);
				} finally {
					setState({
						loadingCreateFriendlyMatch: false
					});
				}
			},
			updateMyMatchAttendance: async (matchId: string, status: RSVPStatusEnum) => {
				const attendanceInfo = await updateMyAttendance(matchId, status);

				setState(ctx => {
					if (ctx.match?.id === matchId) {
						const prevStatus = ctx.attendanceStatus.myStatus;
						let sampleAttendance = [...(ctx.attendanceStatus?.sampleAttendees || [])];
						const myAttendanceIndex = sampleAttendance?.findIndex(x => x?.person?.id);
						if (myAttendanceIndex === -1) {
							sampleAttendance = [...sampleAttendance, attendanceInfo];
						} else {
							sampleAttendance[myAttendanceIndex] = attendanceInfo;
						}

						return {
							attendanceStatus: {
								...ctx.attendanceStatus,
								counts: {
									going:
										prevStatus === RSVPStatusEnum.GOING && status !== RSVPStatusEnum.GOING
											? ctx.attendanceStatus.counts.going - 1
											: prevStatus !== RSVPStatusEnum.GOING && status === RSVPStatusEnum.GOING
												? ctx.attendanceStatus.counts.going + 1
												: ctx.attendanceStatus.counts.going,
									notGoing:
										prevStatus === RSVPStatusEnum.DECLINE && status !== RSVPStatusEnum.DECLINE
											? ctx.attendanceStatus.counts.notGoing - 1
											: prevStatus !== RSVPStatusEnum.DECLINE && status === RSVPStatusEnum.DECLINE
												? ctx.attendanceStatus.counts.notGoing + 1
												: ctx.attendanceStatus.counts.notGoing,
									interested:
										prevStatus === RSVPStatusEnum.INTRESTED && status !== RSVPStatusEnum.INTRESTED
											? ctx.attendanceStatus.counts.interested - 1
											: prevStatus !== RSVPStatusEnum.INTRESTED && status === RSVPStatusEnum.INTRESTED
												? ctx.attendanceStatus.counts.interested + 1
												: ctx.attendanceStatus.counts.interested
								},
								myStatus: status,
								sampleAttendance
							}
						};
					}
					return ctx;
				});
			},
			createCalendarEvent: async (props: CalendarEventModel) => {
				setState({
					loadingCreateFriendlyMatch: true
				});

				try {
					await matchService.createCalendarEvent(props);
					showToast({ text: "Event was successfully created", noIcon: true });
				} catch (err) {
					showToast({ text: "Error while creating a calendar event match" });
					console.error("Error: ", err);
				} finally {
					setState({
						loadingCreateFriendlyMatch: false
					});
				}
			},
			manageCalendarEvents: async (params: ManageCalendarEventsParams) => {
				setState({
					managingCalendarEvents: true
				});

				try {
					await matchService.manageCalendarEvents(params);
				} catch (err) {
					showToast({ text: "Error while managing calendar events" });
					console.error("Error: ", err);
				} finally {
					setState({
						managingCalendarEvents: false
					});
				}
			},
			downloadMatchReports: async (params: { competitionId?: string; groupId?: string; matchId?: string }) => {
				try {
					const { success } = await matchService.downloadMatchReports(params);
					showToast({ text: "You'll get an email with link to reports once it's ready", noIcon: true });
					return success;
				} catch (err) {
					showToast({
						text: "Can't download match reports",
						noIcon: true
					});
					console.error("Error: ", err);
					return null;
				}
			},
			updateMatchPlayersSquad: async (params: {
				matchId: string;
				squadPlayers: {
					add?: {
						playerId: string;
						team: TeamInMatchRole;
						jerseyNumber?: null | number;
					}[];
					remove?: {
						playerId: string;
						team: TeamInMatchRole;
					}[];
				};
			}) => {
				try {
					const { success } = await matchService.updateMatchPlayersSquad(params);
					showToast({ text: "Match players are updated", noIcon: true });
					return success;
				} catch (err) {
					showToast({
						text: "Can't update match players squad",
						noIcon: true
					});
					console.error("Error: ", err);
					return null;
				}
			},
			getMatchPlayerStats: async (params: { matchId: string } & IgnoreStateUpdate) => {
				try {
					const { match } = await matchService.getMatchPlayerStats(params.matchId);
					return match.players;
				} catch (err) {
					showToast({
						text: "Can't get match players stats",
						noIcon: true
					});
					console.error("Error: ", err);
					return [];
				}
			}
		}),
		[matchService, setState, showToast, updateMyAttendance, isShowRoleToasts]
	);

	const getMatchData = useCallback(() => {
		return store;
	}, [store]);

	return { ...methods, getMatchData };
};

export default useMatch;
